import * as SelectPrimitive from "@radix-ui/react-select";
import { Check } from "lucide-react";
import { ImageLoader } from "~/components/ImageLoader";
import { cn } from "~/lib/utils";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { SelectOption } from "./types";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { ClassnamePassthruWrapper } from "../ui/classname-passthru-wrapper";
import { RWA_COIN_ICON } from "~/consts";

// // // //

const SIZE_CLASSES = {
    xs: "min-w-32 text-xs py-0 h-7",
    sm: "min-w-42 text-sm py-0 h-8",
    md: "min-w-52 text-base py-0 h-10",
    lg: "min-w-64 text-lg py-0 h-12",
} as const;

const ICON_SIZES = {
    xs: 14,
    sm: 16,
    md: 20,
    lg: 24,
} as const;

const CHECKMARK_SIZES = {
    xs: "size-4",
    sm: "size-4",
    md: "size-5",
    lg: "size-6",
} as const;

const CHECKMARK_POSITION = {
    xs: "right-2",
    sm: "right-3",
    md: "right-4",
    lg: "right-5",
} as const;

const ITEM_SIZE_CLASSES = {
    xs: "text-xs py-1 pl-2 pr-8",
    sm: "text-sm py-1.5 pl-3 pr-10",
    md: "text-base py-2 pl-4 pr-12",
    lg: "text-lg py-2.5 pl-5 pr-14",
} as const;

const ITEM_DESCRIPTION_CLASSES = {
    xs: "text-xs",
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
} as const;

const OPTIONS_HEIGHT_CLASSES = {
    sm: "max-h-32",
    md: "max-h-60",
    lg: "max-h-96",
    auto: "",
} as const;

// Inset placeholders stack a label above the value, so the trigger grows to
// fit both lines instead of using the single-line heights in SIZE_CLASSES.
const INSET_SIZE_CLASSES = {
    xs: "min-w-32 h-auto py-1",
    sm: "min-w-42 h-auto py-1.5",
    md: "min-w-52 h-auto py-1.5",
    lg: "min-w-64 h-auto py-2",
} as const;

const INSET_LABEL_CLASSES = {
    xs: "text-xs leading-3",
    sm: "text-xs leading-3",
    md: "text-xs leading-4",
    lg: "text-sm leading-4",
} as const;

const INSET_VALUE_CLASSES = {
    xs: "h-4 text-xs",
    sm: "h-5 text-sm",
    md: "h-6 text-base",
    lg: "h-7 text-lg",
} as const;

// // // //

function PingIndicator() {
    return (
        <span
            aria-hidden="true"
            className="relative mx-2 flex size-1.5 shrink-0"
        >
            <span className="absolute inline-flex size-full rounded-full bg-blue-500 animate-ping [animation-duration:3s] motion-reduce:hidden" />
            <span className="relative inline-flex size-1.5 rounded-full bg-blue-500" />
        </span>
    );
}

// // // //

interface Props {
    value: string;
    onChange: (updatedValue: string) => void;
    options: Array<SelectOption>;
    placeholder?: string;
    placeholderPosition?: "default" | "hidden" | "inset";
    disabled?: boolean;
    name?: string;
    className?: string;
    size?: "xs" | "sm" | "md" | "lg";
    optionsContainerHeight?: "sm" | "md" | "lg" | "auto";
    optionsContainerAlign?: "start" | "center" | "end" | undefined;
    testID?: string;
    open?: boolean;
}

export function SelectInput(props: Props) {
    const {
        value,
        onChange,
        options,
        placeholder = "Select an option",
        placeholderPosition = "default",
        disabled,
        name,
        className,
        size = "md",
        optionsContainerHeight = "auto",
        optionsContainerAlign = undefined,
        testID,
        open,
    } = props;

    const iconSize = ICON_SIZES[size];
    // With nothing selected there's no value to label, so an inset placeholder
    // falls back to the "hidden" rendering until a value is picked.
    const insetPlaceholder = placeholderPosition === "inset" && !!value;

    // Define classnames for SelectTrigger
    const insetCss = INSET_SIZE_CLASSES[size];
    const sizeCss = SIZE_CLASSES[size];

    // Define a flag to show the SelectLabel placeholder at the top of the list
    const showSelectLabel =
        placeholderPosition === "default" && value !== undefined;

    return (
        <Select
            value={value}
            onValueChange={(updatedValue) => {
                onChange(updatedValue);
            }}
            disabled={disabled}
            name={name}
            // NOTE - we include props.open primarily for testing open states in Storybook
            open={open}
        >
            <SelectTrigger
                className={cn(
                    "border-border focus:ring-0 focus:ring-offset-0 transition-colors text-left text-secondary-foreground select-none", // Hide focus ring + left-align text
                    className,
                    {
                        [insetCss]: insetPlaceholder,
                        [sizeCss]: !insetPlaceholder,
                        // The trigger line-clamps its direct child span, which
                        // would collapse the stacked label + value into a
                        // single line.
                        "[&>span]:line-clamp-none": insetPlaceholder,
                        // Hover state + cursor pointer when not disabled
                        "cursor-pointer hover:bg-background-hover": !disabled,
                    },
                )}
                data-testid={testID}
            >
                {insetPlaceholder ? (
                    <span className="flex min-w-0 flex-1 flex-col items-start text-left">
                        <span
                            className={cn(
                                "max-w-full truncate font-light text-muted-foreground",
                                INSET_LABEL_CLASSES[size],
                            )}
                        >
                            {placeholder}
                        </span>
                        <span
                            className={cn(
                                "flex min-w-0 max-w-full items-center overflow-hidden select-none [&>span]:line-clamp-1",
                                INSET_VALUE_CLASSES[size],
                            )}
                        >
                            <SelectValue />
                        </span>
                    </span>
                ) : (
                    <SelectValue
                        placeholder={placeholder}
                        className="select-none flex items-center"
                    />
                )}
            </SelectTrigger>
            <SelectContent
                align={optionsContainerAlign}
                className={cn(
                    OPTIONS_HEIGHT_CLASSES[optionsContainerHeight],
                    "z-500",
                )}
            >
                <SelectGroup>
                    {/* Show placeholder as a label if it's not inset and not empty */}
                    {showSelectLabel && (
                        <SelectLabel
                            className={cn(
                                "text-muted-foreground font-light select-none",
                                ITEM_SIZE_CLASSES[size],
                            )}
                        >
                            {placeholder}
                        </SelectLabel>
                    )}

                    {options.map((opt) => (
                        <FeatureFlag key={opt.value} feature={opt.featureFlag}>
                            <SelectPrimitive.Item
                                key={opt.value}
                                value={opt.value}
                                data-testid={opt.testID}
                                className={cn(
                                    "relative flex w-full cursor-default select-none items-center rounded-sm outline-none focus:bg-muted focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                                    ITEM_SIZE_CLASSES[size],
                                )}
                            >
                                <span className="flex flex-col items-start">
                                    <ClassnamePassthruWrapper
                                        className={cn({
                                            "flex items-center":
                                                opt.content.pingIndicator,
                                        })}
                                    >
                                        <SelectPrimitive.ItemText>
                                            <span className="flex items-center gap-2 select-none mr-1 whitespace-nowrap">
                                                {opt.content.icon && (
                                                    <ImageLoader
                                                        src={opt.content.icon}
                                                        alt=""
                                                        width={iconSize}
                                                        height={iconSize}
                                                        className="shrink-0 rounded-full"
                                                        fallbackClassName="bg-white opacity-50"
                                                        fallbackSrc={
                                                            RWA_COIN_ICON
                                                        }
                                                    />
                                                )}
                                                <span className="truncate">
                                                    {opt.content.label}
                                                </span>
                                            </span>
                                        </SelectPrimitive.ItemText>
                                        {/* Render PingIndicator if enabled */}
                                        {opt.content.pingIndicator && (
                                            <PingIndicator />
                                        )}
                                    </ClassnamePassthruWrapper>

                                    {opt.content.description && (
                                        <span
                                            className={cn(
                                                "text-muted-foreground font-normal",
                                                ITEM_DESCRIPTION_CLASSES[size],
                                            )}
                                        >
                                            {opt.content.description}
                                        </span>
                                    )}
                                </span>

                                <span
                                    className={cn(
                                        "absolute flex size-3.5 items-center justify-center",
                                        CHECKMARK_POSITION[size],
                                    )}
                                >
                                    <SelectPrimitive.ItemIndicator>
                                        <Check
                                            className={cn(
                                                "text-primary",
                                                CHECKMARK_SIZES[size],
                                            )}
                                        />
                                    </SelectPrimitive.ItemIndicator>
                                </span>
                            </SelectPrimitive.Item>
                        </FeatureFlag>
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
    );
}
